/**
 * CreditItem[] をプレーンテキスト（エンドロール風）に整形するユーティリティ。
 * - JP / EN のいずれか片方を出力（足りない側はもう片方にフォールバック）。
 * - section: `=== タイトル ===` + 任意で note。
 * - subsection: `— タイトル —`。
 * - entry: 役割と表示名をタブ区切りで出力（SNS / エディタ / 表計算で扱いやすい TSV 風）。
 * - space: 指定行数ぶん空行。
 * - track / image: 現状未対応（スキップ）。
 *
 * 連続する空行は最大 1 行に圧縮し、行間を揃える。
 */
import type { CreditItem } from "../../app/services/credits";

/** クレジット表示の言語切替値 */
export type Language = "JP" | "EN";

/**
 * JP / EN のうち指定された言語の値を返す。空のときは反対言語にフォールバックする。
 */
export const pickJpEn = (
  jp: string | undefined,
  en: string | undefined,
  lang: Language,
): string => {
  if (lang === "JP") return jp || en || "";
  return en || jp || "";
};

export interface BuildCreditPlainTextOptions {
  items: CreditItem[];
  language: Language;
  /** 先頭に作品タイトルを差し込む（任意） */
  projectTitle?: string;
}

/** 直前の行が非空のときのみ空行を 1 行追加する。 */
const pushBlankLine = (lines: string[]): void => {
  if (lines.length > 0 && lines[lines.length - 1] !== "") lines.push("");
};

export function buildCreditPlainText(
  options: BuildCreditPlainTextOptions,
): string {
  const { items, language, projectTitle } = options;

  const sorted = items.slice().sort((a, b) => a.order - b.order);

  const lines: string[] = [];

  if (projectTitle && projectTitle.trim() !== "") {
    lines.push(projectTitle.trim());
    lines.push("");
  }

  for (const item of sorted) {
    switch (item.item_type) {
      case "section": {
        const s = item.section;
        const title = pickJpEn(s?.title, s?.title_en, language);
        const note = pickJpEn(s?.note, s?.note_en, language);
        pushBlankLine(lines);
        if (title) lines.push(`=== ${title} ===`);
        if (note) lines.push(note);
        lines.push("");
        break;
      }
      case "subsection": {
        const s = item.subsection;
        const title = pickJpEn(s?.title, s?.title_en, language);
        if (title) {
          pushBlankLine(lines);
          lines.push(`— ${title} —`);
          lines.push("");
        }
        break;
      }
      case "entry": {
        const e = item.entry;
        const role = pickJpEn(e?.role, e?.role_en, language);
        const name = pickJpEn(e?.display_name, e?.display_name_en, language);
        // タブ区切りで列を維持（role が空でも先頭タブを保持）。
        lines.push(`${role}\t${name}`);
        break;
      }
      case "space": {
        const n = Math.max(1, item.space?.lines ?? 1);
        for (let i = 0; i < n; i++) lines.push("");
        break;
      }
      // track / image はテキスト出力非対応（現状はスキップ）
      default:
        break;
    }
  }

  // 連続する空行を 1 行に圧縮
  const compressed: string[] = [];
  for (const line of lines) {
    if (line === "" && compressed.length > 0 && compressed[compressed.length - 1] === "") {
      continue;
    }
    compressed.push(line);
  }

  // 末尾の空行を除去
  while (compressed.length > 0 && compressed[compressed.length - 1] === "") {
    compressed.pop();
  }

  return compressed.join("\n");
}
